import type { SafeInsets } from "../platform/types";

export const uiScale = (width: number, height: number): number =>
  Math.max(1, Math.min(width / 390, height / 844, 1.5));

export const scaleRect = (r: ButtonRect, s: number): ButtonRect => ({
  x: r.x * s,
  y: r.y * s,
  width: r.width * s,
  height: r.height * s,
});
export const unscaleSafe = (safe: SafeInsets, s: number): SafeInsets => ({
  top: safe.top / s,
  bottom: safe.bottom / s,
  left: safe.left / s,
  right: safe.right / s,
});

export const DESIGN_WIDTH = 360;
export const DESIGN_HEIGHT = 640;

export interface GameViewport {
  x: number;
  y: number;
  width: number;
  height: number;
  scale: number;
}

export interface ButtonRect {
  x: number; // screen px
  y: number; // screen px
  width: number; // screen px
  height: number; // screen px
}

export interface LayoutRects {
  pause: ButtonRect;
  mute: ButtonRect;
  ammo: ButtonRect;
  dodge: ButtonRect;
  block: ButtonRect;
  dodgeArt: ButtonRect;
  blockArt: ButtonRect;
  resume: ButtonRect;
  forfeit: ButtonRect;
  play: ButtonRect;
  playAgain: ButtonRect;
  home: ButtonRect;
  badgeOpp: ButtonRect;
  badgeYou: ButtonRect;
  avatarOpp: ButtonRect;
  avatarYou: ButtonRect;
  timer: ButtonRect;
  dots: ButtonRect;
  roundLabel: ButtonRect;
  hint: ButtonRect;
}

export function pointInRect(x: number, y: number, rect: ButtonRect): boolean {
  return (
    x >= rect.x - 1e-9 &&
    x <= rect.x + rect.width + 1e-9 &&
    y >= rect.y - 1e-9 &&
    y <= rect.y + rect.height + 1e-9
  );
}

export function computeGameViewport(
  width: number,
  height: number,
  safeInsets: SafeInsets,
): GameViewport {
  void safeInsets;
  return { x: 0, y: 0, width, height, scale: 1 };
}

export function computeLayoutRects(
  viewport: GameViewport,
  safeInsets: SafeInsets,
  screenWidth: number,
  screenHeight: number,
  leftHanded = false,
): LayoutRects {
  const s = uiScale(screenWidth, screenHeight);
  if (s > 1) {
    const base = computeLayoutRects(
      viewport,
      unscaleSafe(safeInsets, s),
      screenWidth / s,
      screenHeight / s,
      leftHanded,
    );
    const scaled = Object.fromEntries(
      (Object.keys(base) as (keyof LayoutRects)[]).map((id) => [
        id,
        scaleRect(base[id], s),
      ]),
    ) as unknown as LayoutRects;
    const buttonWidth = Math.min(screenWidth * 0.7, 420);
    scaled.resume = {
      x: (screenWidth - buttonWidth) / 2,
      y: screenHeight - safeInsets.bottom - 124 * s - 32,
      width: buttonWidth,
      height: 56 * s,
    };
    scaled.forfeit = {
      ...scaled.resume,
      y: screenHeight - safeInsets.bottom - 56 * s - 32,
    };
    return scaled;
  }
  void viewport;
  const C = screenWidth / 2,
    T = safeInsets.top + 10,
    B = screenHeight - safeInsets.bottom;
  const left = Math.max(safeInsets.left, C - 240),
    right = Math.min(screenWidth - safeInsets.right, C + 240);
  const L = left + 12,
    R = right - 12;
  const rect = (
    x: number,
    y: number,
    width: number,
    height: number,
  ): ButtonRect => ({ x, y, width, height });
  const expand = (r: ButtonRect, n: number): ButtonRect =>
    rect(r.x - n, r.y - n, r.width + 2 * n, r.height + 2 * n);
  const landscape = screenWidth > screenHeight;
  // v3 (locked R3 layout): 76 px circles centred B-66 (y744 on 390x844 with a
  // 34 px home-bar inset); the label pill ends at B-6.
  const dodgeArt = rect(
      landscape ? safeInsets.left : left + 16,
      B - 104,
      76,
      76,
    ),
    blockArt = rect(
      landscape ? safeInsets.left + 288 : right - 92,
      B - 104,
      76,
      76,
    );
  // Touch rect = circle plus the label pill below it.
  const touch = (r: ButtonRect): ButtonRect =>
    rect(r.x - 8, r.y - 8, r.width + 16, 110);
  const play = rect(
    C - Math.min(332, right - left - 64) / 2,
    B - 160,
    Math.min(332, right - left - 64),
    72,
  );
  return {
    pause: rect(left + 14, T + 70, 44, 44),
    mute: rect(right - 58, T + 70, 44, 44),
    ammo: rect(landscape ? safeInsets.left + 106 : C - 82, B - 88, 164, 44),
    dodge: touch(leftHanded ? blockArt : dodgeArt),
    block: touch(leftHanded ? dodgeArt : blockArt),
    dodgeArt: leftHanded ? blockArt : dodgeArt,
    blockArt: leftHanded ? dodgeArt : blockArt,
    resume: rect(
      C - Math.min(0.7 * screenWidth, 420) / 2,
      B - 180,
      Math.min(0.7 * screenWidth, 420),
      56,
    ),
    forfeit: rect(
      C - Math.min(0.7 * screenWidth, 420) / 2,
      B - 112,
      Math.min(0.7 * screenWidth, 420),
      56,
    ),
    play: expand(play, 8),
    playAgain: rect(
      C - (right - left - 80) / 2,
      B - 147,
      right - left - 80,
      68,
    ),
    home: rect(C - 76, B - 61, 152, 44),
    badgeOpp: rect(L, T + 8, 150, 50),
    badgeYou: rect(R - 150, T + 8, 150, 50),
    avatarOpp: rect(L - 4, T, 56, 56),
    avatarYou: rect(R - 60, T, 56, 56),
    timer: rect(
      C - Math.min(96, (76 * screenWidth) / 384) / 2,
      T + 4,
      Math.min(96, (76 * screenWidth) / 384),
      46,
    ),
    dots: rect(C - 22, T + 66, 44, 11),
    roundLabel: rect(C - 50, T + 78, 100, 16),
    hint: rect(C - 65, B - 112, 130, 28),
  };
}
